import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
    Table, Button, Spin, Alert, Typography, Modal, Pagination, Tag, Select, Row, Col, DatePicker, message, Tooltip, List as AntdList, Card, Divider, Flex
} from 'antd';
import {
    EyeOutlined, BarChartOutlined, FilterOutlined, CalendarOutlined, UserOutlined, ApartmentOutlined, GlobalOutlined, CheckCircleOutlined, WarningOutlined,
    InfoCircleOutlined, TeamOutlined, RiseOutlined, MedicineBoxOutlined, DownloadOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isoWeek from 'dayjs/plugin/isoWeek';
import { useSelector, useDispatch } from 'react-redux';

import { getVisitStatistics, exportVisitStatistics, getVisitsByDelegate, getDistinctFilterOptionsForDelegate, getMissedVisitsGrouped } from '../../services/visitService'; 
import { getDelegatesForPlanning } from '../../services/delegateService'; 
import { fetchPublicHolidays, fetchDelegateLeaves, fetchCycles } from '../../redux/slices/planning/planningThunks';

const OtherInteractionStatusEnum = {
    VISITED_BY_OTHER: 'VISITED_BY_OTHER',
    ASSIGNED_TO_OTHER_NOT_VISITED: 'ASSIGNED_TO_OTHER_NOT_VISITED',
    NOT_ASSIGNED_TO_OTHERS_IN_PERIOD: 'NOT_ASSIGNED_TO_OTHERS_IN_PERIOD',
};

dayjs.extend(isBetween);
dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);
dayjs.extend(isoWeek);

const { Title, Text } = Typography;
const formatDate = (dateString) => dateString ? (dayjs(dateString).isValid() ? dayjs(dateString).format('DD/MM/YYYY') : 'Date Inv.') : 'N/A';

const DOCTOR_TYPE_OPTIONS = [ { value: 'all', label: 'Tous Types' }, { value: '1', label: 'Hospitalier' }, { value: '2', label: 'Privé/Mixte' }];
const AVG_VISIT_RANGE_OPTIONS = [{value:'all',label:'Toutes'},{value:'6',label:'6-6.9'},{value:'7',label:'7-7.9'},{value:'8',label:'8-8.9'},{value:'9',label:'9-9.9'},{value:'10',label:'10-10.9'},{value:'11',label:'11-11.9'},{value:'12',label:'+12'}];
const OBJECTIVE_RANGE_OPTIONS = [{value:'all',label:'Tous %'},{value:'plus50',label:'>50%'},{value:'60',label:'60-69%'},{value:'70',label:'70-79%'},{value:'80',label:'80-89%'},{value:'90',label:'90-99%'},{value:'100',label:'>=100%'},{value:'plus100',label:'>100%'}];

const VisitManagementPage = () => {
    const dispatch = useDispatch();
    const isMounted = useRef(true);

    const [stats, setStats] = useState([]);
    const [loadingStats, setLoadingStats] = useState(true);
    const [errorStats, setErrorStats] = useState(null);
    const [delegatesList, setDelegatesList] = useState([]);
    const [loadingDelegates, setLoadingDelegates] = useState(true);

    const [exporting, setExporting] = useState(false);
    
    const defaultStartDate = dayjs().subtract(1, 'month').startOf('month');
    const defaultEndDate = dayjs().endOf('month');
    const [selectedStartDate, setSelectedStartDate] = useState(defaultStartDate);
    const [selectedEndDate, setSelectedEndDate] = useState(defaultEndDate);
    const [selectedDelegateIdsForStats, setSelectedDelegateIdsForStats] = useState([]);
    const [selectedDoctorTypeForStats, setSelectedDoctorTypeForStats] = useState('all');
    const [selectedAvgVisitRange, setSelectedAvgVisitRange] = useState('all');
    const [selectedObjectiveRange, setSelectedObjectiveRange] = useState('all');

    const [isModalVisible, setIsModalVisible] = useState(false);
    const [modalDelegateName, setModalDelegateName] = useState('');
    const [modalDelegateId, setModalDelegateId] = useState(null);
    const [delegateVisits, setDelegateVisits] = useState([]);
    const [loadingModalVisits, setLoadingModalVisits] = useState(false);
    const [modalVisitsPagination, setModalVisitsPagination] = useState({ current: 1, pageSize: 10, total: 0 });
    const [errorModalVisits, setErrorModalVisits] = useState(null);

    const [modalFilterOptions, setModalFilterOptions] = useState({ persons: [], specialties: [], regions: [] });
    const [loadingModalFilterOptions, setLoadingModalFilterOptions] = useState(false);
    const [selectedModalDoctorId, setSelectedModalDoctorId] = useState('all');
    const [selectedModalSpecialtyId, setSelectedModalSpecialtyId] = useState('all');
    const [selectedModalRegionId, setSelectedModalRegionId] = useState('all');
    
    // --- NOUVEAU : Filtre par Type dans le Modal ---
    const [selectedModalPersonType, setSelectedModalPersonType] = useState('all');
    // ----------------------------------------------

    const [missedDoctorsData, setMissedDoctorsData] = useState([]);
    const [loadingMissedDoctors, setLoadingMissedDoctors] = useState(false);
    const [selectedMissedStatusFilter, setSelectedMissedStatusFilter] = useState('all');

    useEffect(() => { isMounted.current = true; return () => { isMounted.current = false; }; }, []);
    useEffect(() => { dispatch(fetchPublicHolidays()); dispatch(fetchDelegateLeaves()); dispatch(fetchCycles()); }, [dispatch]);
    useEffect(() => { 
        setLoadingDelegates(true); 
        getDelegatesForPlanning()
            .then(r => { 
                if(isMounted.current) {
                    const data = Array.isArray(r?.data) ? r.data : [];
                    setDelegatesList(data); 
                }
            })
            .catch(() => {if(isMounted.current) message.error("Erreur chargement délégués");})
            .finally(() => { if(isMounted.current) setLoadingDelegates(false); }); 
    }, []);

    useEffect(() => {
        if (selectedStartDate?.isValid() && selectedEndDate?.isValid()) {
            setLoadingStats(true); setErrorStats(null);
            const sd = selectedStartDate.format('YYYY-MM-DD'); 
            const ed = selectedEndDate.format('YYYY-MM-DD');
            const docType = selectedDoctorTypeForStats === 'all' ? undefined : selectedDoctorTypeForStats;
            const delIds = selectedDelegateIdsForStats.length > 0 ? selectedDelegateIdsForStats.join(',') : undefined;
            getVisitStatistics(sd, ed, docType, delIds)
                .then(r => { if(isMounted.current) setStats(Array.isArray(r?.data) ? r.data : (Array.isArray(r) ? r : [])); })
                .catch(e => { if(isMounted.current) setErrorStats(e?.response?.data?.message || "Erreur stats"); })
                .finally(() => { if(isMounted.current) setLoadingStats(false); });
        } else { setStats([]); setLoadingStats(false); }
    }, [selectedStartDate, selectedEndDate, selectedDoctorTypeForStats, selectedDelegateIdsForStats]);

    const { current: currentPageModal, pageSize: pageSizeModal } = modalVisitsPagination;
    useEffect(() => {
        if (isModalVisible && modalDelegateId !== null) {
            setLoadingModalVisits(true); setErrorModalVisits(null);
            const filters = {
                startDate: selectedStartDate?.isValid() ? selectedStartDate.format('YYYY-MM-DD') : undefined,
                endDate: selectedEndDate?.isValid() ? selectedEndDate.format('YYYY-MM-DD') : undefined,
                personId: selectedModalDoctorId === 'all' ? undefined : selectedModalDoctorId,
                specialtyId: selectedModalSpecialtyId === 'all' ? undefined : selectedModalSpecialtyId,
                regionId: selectedModalRegionId === 'all' ? undefined : selectedModalRegionId,
                doctorType: selectedDoctorTypeForStats === 'all' ? undefined : selectedDoctorTypeForStats,
                personType: selectedModalPersonType === 'all' ? undefined : selectedModalPersonType, // <-- Utilisation du nouveau filtre
            };
            getVisitsByDelegate(modalDelegateId, currentPageModal, pageSizeModal, filters.startDate, filters.endDate, filters.personId, filters.specialtyId, filters.regionId, filters.doctorType, filters.personType)
                .then(res => { if(isMounted.current) { setDelegateVisits(res?.data?.data || []); setModalVisitsPagination(p=>({...p, total: res?.data?.total || 0}));}})
                .catch(err => { if(isMounted.current) setErrorModalVisits(err?.response?.data?.message || "Err visites modal"); })
                .finally(() => { if(isMounted.current) setLoadingModalVisits(false); });
        }
    }, [isModalVisible, modalDelegateId, currentPageModal, pageSizeModal, selectedStartDate, selectedEndDate, selectedModalDoctorId, selectedModalSpecialtyId, selectedModalRegionId, selectedDoctorTypeForStats, selectedModalPersonType]);

     useEffect(() => {
        if (isModalVisible && modalDelegateId != null && selectedStartDate?.isValid() && selectedEndDate?.isValid()) {
             setLoadingModalFilterOptions(true);
             const sd = selectedStartDate.format('YYYY-MM-DD'); const ed = selectedEndDate.format('YYYY-MM-DD');
             getDistinctFilterOptionsForDelegate(modalDelegateId, sd, ed)
                .then(res => { if(isMounted.current) setModalFilterOptions(res.data || {persons:[],specialties:[],regions:[]}); })
                .catch(() => { if(isMounted.current) message.error("Erreur options filtre modal."); })
                .finally(() => { if(isMounted.current) setLoadingModalFilterOptions(false); });
        }
    }, [isModalVisible, modalDelegateId, selectedStartDate, selectedEndDate]); 
    
    useEffect(() => {
        if (isModalVisible && modalDelegateId != null && selectedStartDate?.isValid() && selectedEndDate?.isValid()) {
            setLoadingMissedDoctors(true); setMissedDoctorsData([]);
            const apiFilters = {
                startDate: selectedStartDate.format('YYYY-MM-DD'),
                endDate: selectedEndDate.format('YYYY-MM-DD'),
                delegateIds: String(modalDelegateId),
                doctorType: selectedDoctorTypeForStats === 'all' ? undefined : selectedDoctorTypeForStats,
                personId: selectedModalDoctorId === 'all' ? undefined : selectedModalDoctorId,
            };
            getMissedVisitsGrouped(apiFilters)
                .then(res => { if(isMounted.current) { setMissedDoctorsData(res?.data || []); }})
                .catch(err => { if(isMounted.current) message.error(err?.response?.data?.message || "Err non visités"); })
                .finally(() => { if(isMounted.current) setLoadingMissedDoctors(false); });
        } else if (!isModalVisible) {
            setMissedDoctorsData([]);
        }
    }, [isModalVisible, modalDelegateId, selectedStartDate, selectedEndDate, selectedDoctorTypeForStats, selectedModalDoctorId]);

    const handleShowVisits = useCallback((record) => { 
        setModalDelegateId(record.delegate_id); 
        setModalDelegateName(record.delegate_name || `ID: ${record.delegate_id}`); 
        setModalVisitsPagination({ current: 1, pageSize: 10, total: 0 }); 
        setDelegateVisits([]); setErrorModalVisits(null); 
        setSelectedModalDoctorId('all'); setSelectedModalSpecialtyId('all'); setSelectedModalRegionId('all'); 
        setMissedDoctorsData([]); setSelectedMissedStatusFilter('all');
        setSelectedModalPersonType('all'); // Reset
        setIsModalVisible(true); 
    }, []);
    const handleModalCancel = useCallback(() => setIsModalVisible(false), []);
    const handleModalPageChange = useCallback((p,ps) => setModalVisitsPagination(prev=>({...prev,current:p,pageSize:ps})), []);
    const handleDelegateStatsFilterChange = useCallback((v) => setSelectedDelegateIdsForStats(v || []), []);
    const handleDoctorTypeStatsFilterChange = useCallback((v) => setSelectedDoctorTypeForStats(v || 'all'), []);
    const handleStartDateChange = useCallback((d) => setSelectedStartDate(d ? dayjs(d) : null), []);
    const handleEndDateChange = useCallback((d) => setSelectedEndDate(d ? dayjs(d) : null), []);
    const handleModalDoctorFilterChange = useCallback((v) => { setSelectedModalDoctorId(v||'all'); setModalVisitsPagination(p=>({...p,current:1}));}, []);
    const handleModalSpecialtyFilterChange = useCallback((v) => { setSelectedModalSpecialtyId(v||'all'); setModalVisitsPagination(p=>({...p,current:1}));}, []);
    const handleModalRegionFilterChange = useCallback((v) => { setSelectedModalRegionId(v||'all'); setModalVisitsPagination(p=>({...p,current:1}));}, []);
    // --- HANDLER TYPE PERSONNE ---
    const handleModalPersonTypeChange = useCallback((v) => { setSelectedModalPersonType(v||'all'); setModalVisitsPagination(p=>({...p,current:1}));}, []);
    // -----------------------------
    const handleMissedStatusFilterChange = useCallback((value) => setSelectedMissedStatusFilter(value === undefined ? 'all' : value), []);
    const handleAvgVisitRangeChange = useCallback((value) => { setSelectedAvgVisitRange(value || 'all'); }, []);
    const handleObjectiveRangeChange = useCallback((value) => { setSelectedObjectiveRange(value || 'all'); }, []);

    const handleExport = useCallback(async () => {
        if (!selectedStartDate?.isValid() || !selectedEndDate?.isValid()) {
            message.warning("Veuillez sélectionner une période valide pour l'export.");
            return;
        }
        setExporting(true);
        try {
            const sd = selectedStartDate.format('YYYY-MM-DD');
            const ed = selectedEndDate.format('YYYY-MM-DD');
            const docType = selectedDoctorTypeForStats === 'all' ? undefined : selectedDoctorTypeForStats;
            const delIds = selectedDelegateIdsForStats.length > 0 ? selectedDelegateIdsForStats.join(',') : undefined;

            const response = await exportVisitStatistics(sd, ed, docType, delIds);

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            const filename = `statistiques-visites-${dayjs().format('YYYY-MM-DD')}.csv`;
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
            window.URL.revokeObjectURL(url); 

            message.success("L'export a été téléchargé avec succès !");
        } catch (error) {
            console.error("Erreur lors de l'export CSV:", error);
            message.error("Une erreur est survenue lors de la génération de l'export.");
        } finally {
            setExporting(false);
        }
    }, [selectedStartDate, selectedEndDate, selectedDoctorTypeForStats, selectedDelegateIdsForStats]);

    const processedStats = useMemo(() => {
        return (Array.isArray(stats) ? stats : []).map(stat => ({
            ...stat,
            workingDaysCalculated: stat.jours_realises,
            visitObjectiveAverage: stat.moyenne_visites_par_jour_realise,
            objectivePercentage: stat.objectif_pourcentage,
            _objectiveInfo: {
                cycleName: stat._debug_cycle_name || "N/A",
                weeklyTarget: 'N/A',
                visitsDone: stat.total_visites,
                workingDays: stat.jours_realises,
                theoreticalTarget: stat._debug_theoretical_target?.toFixed(1) || 'N/A'
            }
        }));
    }, [stats]);

    const filteredProcessedStats = useMemo(() => {
        let result = processedStats;
        if (selectedAvgVisitRange !== 'all') {
            result = result.filter(stat => {
                const avg = stat.visitObjectiveAverage; 
                if (avg == null || isNaN(avg)) return false; 
                if (selectedAvgVisitRange === 'plus12') return avg > 12;
                const numericFilterVal = parseInt(selectedAvgVisitRange, 10);
                if (!isNaN(numericFilterVal)) return avg >= numericFilterVal && avg < (numericFilterVal + 1);
                return false;
            });
        }
        if (selectedObjectiveRange !== 'all') {
             result = result.filter(stat => { 
                const percentage = stat.objectivePercentage; if (percentage == null || isNaN(percentage)) return false; 
                if (selectedObjectiveRange === 'plus100') return percentage > 100;
                if (selectedObjectiveRange === '100') return percentage >= 100;
                const numericObjectiveVal = parseInt(selectedObjectiveRange, 10);
                if(!isNaN(numericObjectiveVal)) {
                     if (selectedObjectiveRange === 'plus50') return percentage >= 50;
                     return percentage >= numericObjectiveVal && percentage < (numericObjectiveVal + 10);
                }
                return false; 
            });
        }
        return result;
    }, [processedStats, selectedAvgVisitRange, selectedObjectiveRange]);

    const filteredMissedDoctorGroups = useMemo(() => {
        if (!missedDoctorsData || missedDoctorsData.length === 0) return [];
        if (selectedMissedStatusFilter === 'all') return missedDoctorsData;
        return missedDoctorsData.map(doctorGroup => {
            const filteredAssignments = doctorGroup.missedAssignments.filter(
                assignment => assignment.otherInteractionStatus === selectedMissedStatusFilter
            );
            return filteredAssignments.length > 0 ? { ...doctorGroup, missedAssignments: filteredAssignments } : null;
        }).filter(Boolean);
    }, [missedDoctorsData, selectedMissedStatusFilter]);

    const statsColumns = [
        { title: 'Délégué', dataIndex: 'delegate_name', key: 'delegate_name', sorter: (a,b)=>(a.delegate_name||'').localeCompare(b.delegate_name||''), fixed: 'left', width: 180 },
        { title: 'Tot. Visites', dataIndex: 'total_visites', key: 'total_visites', sorter: (a,b)=>(a.total_visites||0)-(b.total_visites||0), align: 'center', width: 100 },
        { title: 'Jours de Terrain', dataIndex: 'jours_travailles', key: 'jours_travailles_api', align: 'center', width: 100 },
        { title: 'Jours de Travail', dataIndex: 'workingDaysCalculated', key: 'workingDaysCalculated', align: 'center', width: 110, render: v => v != null ? <Text strong style={{color: v > 0 ? '#1890ff' : '#bfbfbf'}}>{v}</Text> : <Text type="secondary">N/D</Text>},
        { title: 'Moy/J', dataIndex: 'visitObjectiveAverage', key: 'visitObjectiveAverage', align: 'right', width: 110, render: v => v != null ? v.toFixed(2) : <Text type="secondary">N/D</Text>},
        { title: '% Obj.', dataIndex: 'objectivePercentage', key: 'objectivePercentage', align: 'right', width: 110, 
            render: (p, rec) => {
                if (p==null) return <Tooltip title={rec._objectiveInfo?.cycleName === "Aucun cycle actif" ? "Aucun cycle actif pour la période" : "Calcul impossible"}><Text type="secondary">N/D</Text></Tooltip>;
                const color = p >= 100 ? 'green' : p >= 80 ? 'blue '  : p >= 50 ? 'orange' : 'red';
                const className = p >= 100 ? 'obj-visite visite-green' : p >= 80 ? 'obj-visite visite-blue' : p >= 50 ? 'obj-visite visite-orange' : 'obj-visite visite-red';
                return <Tooltip title={`Cycle: ${rec._objectiveInfo?.cycleName}\nObj/Sem: ${rec._objectiveInfo?.weeklyTarget}\nJ.Trav: ${rec._objectiveInfo?.workingDays}\nObj.Théo: ${rec._objectiveInfo?.theoreticalTarget}\nVisites: ${rec._objectiveInfo?.visitsDone}`}><Tag color={color} className={className}>{p === Infinity ? ">1000" : p.toFixed(1)}%</Tag></Tooltip>;
            }
        },
        { title: 'Actions', key: 'actions', render: (_,r)=>(<Button type="primary" icon={<EyeOutlined />} onClick={()=>handleShowVisits(r)} size="small">Détails</Button>), align:'center', fixed:'right', width:100 }
    ];
    
    // --- COLONNES MODAL CORRIGÉES ---
    const modalVisitColumns = [
        { title: 'Date', dataIndex: 'date', key: 'date', render: formatDate, width: 100, sorter:(a,b)=>dayjs(a.date).valueOf()-dayjs(b.date).valueOf(), defaultSortOrder:'descend'},
        { 
            title: 'Nom', // Changé "Médecin" en "Nom" pour être générique
            key: 'personName', 
            width: 180, 
            ellipsis: true, 
            render: (_,r) => {
                const p = r.person;
                if (!p) return 'N/A';
                // Si c'est une pharmacie avec un nom, on l'affiche
                if (p.type === 2 && p.pharmacien?.pharmacyName) return `${p.pharmacien.pharmacyName} (${p.firstname} ${p.lastname})`;
                return `${p.firstname} ${p.lastname}`.trim();
            }
        },
        { 
            title: 'Spécialité', 
            key: 'specialty', 
            width: 120, 
            ellipsis: true, 
            render: (_,r) => {
                const p = r.person;
                // Si Pharmacie (Type 2) -> Tag Vert
                if (p?.type === 2) return <Tag color="green">Pharmacie</Tag>;
                // Si Grossiste (Type 3) -> Tag Orange
                if (p?.type === 3) return <Tag color="orange">Grossiste</Tag>;
                // Sinon (Médecin), on affiche la spécialité
                return p?.doctor?.specialty?.name || '-';
            }
        },
        { title: 'Région', key: 'region', width: 120, ellipsis: true, render: (_,r)=>r.person?.region?.name||'-'},
        { 
            title: 'Produits', 
            dataIndex: 'reportProducts', 
            key: 'products', 
            width: 180, 
            ellipsis: true, 
            render: (reportProducts) => {
                if (!reportProducts || reportProducts.length === 0) return <Text type="secondary">-</Text>;
                return (
                    <Flex wrap="wrap" gap="small">
                        {reportProducts.slice(0, 2).map(item => (
                            item.product?.name ? 
                            <Tag color="blue" key={item.product?.id || Math.random()}>
                                {item.product.name}
                            </Tag> 
                            : null
                        ))}
                        {reportProducts.length > 2 && <Tag>...</Tag>}
                    </Flex>
                );
            } 
        },
        { 
            title: 'Échantillons', 
            dataIndex: 'reportSamples', 
            key: 'samples', 
            width: 180, 
            ellipsis: true, 
            render: (reportSamples) => {
                if (!reportSamples || reportSamples.length === 0) return <Text type="secondary">-</Text>;
                return (
                    <Flex wrap="wrap" gap="small">
                        {reportSamples.slice(0, 2).map(item => (
                            item.sample?.name ?
                            <Tag color="green" key={item.sample?.id || Math.random()}>
                                {`${item.sample.name} (x${item.quantity || 1})`}
                            </Tag>
                            : null
                        ))}
                        {reportSamples.length > 2 && <Tag>...</Tag>}
                    </Flex>
                );
            } 
        },
    ];

    const doctorModalOptions = useMemo(() => {
        return [{value:'all',label:'Tous'}, ...(modalFilterOptions.persons||[]).map(p=>({value:String(p.id), label:p.name||`ID:${p.id}`}))];
    }, [modalFilterOptions.persons]);

    const specialtyModalOptions = useMemo(() => {
        return [{value:'all',label:'Toutes'}, ...(modalFilterOptions.specialties||[]).map(s=>({value:String(s.id), label:s.name||'?' }))];
    }, [modalFilterOptions.specialties]);

    const regionModalOptions = useMemo(() => {
        return [{value:'all',label:'Toutes'}, ...(modalFilterOptions.regions||[]).map(r=>({value:String(r.id), label:r.name||'?' }))];
    }, [modalFilterOptions.regions]);

    if (loadingDelegates) {
        return <div style={{display:'flex',justifyContent:'center',alignItems:'center',height:'80vh'}}><Spin size="large" tip="Chargement des délégués..."/></div>;
    }

    return (
        <>
            <Title level={2} style={{ marginBottom: 24 }}><BarChartOutlined style={{marginRight:8}}/>Statistiques Visites {selectedStartDate && selectedEndDate && <Text type="secondary" style={{fontSize:'0.8em', fontWeight:'normal'}}>({selectedStartDate.format('DD/MM/YY')} - {selectedEndDate.format('DD/MM/YY')})</Text>}</Title>
            <Card style={{marginBottom:24}}>
                <Row gutter={[16, 16]} align="bottom">
                    <Col xs={24} sm={12} md={8} lg={5}>
                        <Text strong><FilterOutlined /> Délégué(s):</Text>
                        <Select 
                            mode="multiple" 
                            style={{width:'100%'}} 
                            placeholder="Tous" 
                            loading={loadingDelegates} 
                            onChange={handleDelegateStatsFilterChange} 
                            value={selectedDelegateIdsForStats} 
                            allowClear 
                            options={delegatesList.map(d=>({
                                value: String(d.id),
                                label: `${d.firstname || ''} ${d.lastname || ''}`.trim() || `ID:${d.id}`
                            }))} 
                            maxTagCount="responsive"
                        />
                    </Col>
                    <Col xs={12} sm={8} md={6} lg={3}><Text strong><CalendarOutlined /> Du:</Text><DatePicker style={{width:'100%'}} onChange={handleStartDateChange} value={selectedStartDate} format="DD/MM/YYYY" allowClear picker="date" /></Col>
                    <Col xs={12} sm={8} md={6} lg={3}><Text strong><CalendarOutlined /> Au:</Text><DatePicker style={{width:'100%'}} onChange={handleEndDateChange} value={selectedEndDate} format="DD/MM/YYYY" allowClear picker="date" /></Col>
                    <Col xs={24} sm={8} md={6} lg={4}><Text strong><MedicineBoxOutlined style={{marginRight:4}}/>Type Médecin:</Text><Select style={{width:'100%'}} value={selectedDoctorTypeForStats} onChange={handleDoctorTypeStatsFilterChange} options={DOCTOR_TYPE_OPTIONS}/></Col>
                    <Col xs={12} sm={6} md={4} lg={3}><Text strong><RiseOutlined /> Moy. Vis/J:</Text><Select style={{width:'100%'}} placeholder="Toutes" value={selectedAvgVisitRange} onChange={handleAvgVisitRangeChange} allowClear options={AVG_VISIT_RANGE_OPTIONS} /></Col>
                    <Col xs={12} sm={6} md={4} lg={3}><Text strong><CheckCircleOutlined /> % Objectif:</Text><Select style={{width:'100%'}} placeholder="Toutes" value={selectedObjectiveRange} onChange={handleObjectiveRangeChange} allowClear options={OBJECTIVE_RANGE_OPTIONS} /></Col>
                     <Col xs={24} sm={8} md={6} lg={3} style={{ display: 'flex', alignItems: 'flex-end' }}>
                        <Button 
                            type="primary" 
                            icon={<DownloadOutlined />} 
                            onClick={handleExport}
                            loading={exporting}
                            disabled={loadingStats}
                            style={{width: '100%'}}
                        >
                            Exporter
                        </Button>
                    </Col>
                </Row>
            </Card>

            {errorStats && <Alert message={errorStats} type="error" showIcon closable style={{ marginBottom: 16 }} />}
            <Spin spinning={loadingStats} tip="Chargement statistiques...">
                <Table  columns={statsColumns} dataSource={filteredProcessedStats} rowKey="delegate_id" pagination={{pageSize:15,showSizeChanger:true,pageSizeOptions:['15','30','50','100'],showTotal:(t,r)=>`${r[0]}-${r[1]} sur ${t}`}} size="middle" scroll={{x:'max-content'}} />
            </Spin>

            <Modal 
                title={<Title level={4} style={{ margin: 0 }}>Détails pour {modalDelegateName || 'Délégué'}</Title>} 
                open={isModalVisible} onCancel={handleModalCancel} footer={[<Button key="close" onClick={handleModalCancel}>Fermer</Button>]} 
                width='95%' style={{ top: 20 }} destroyOnClose maskClosable={false}
            >
                 <Row gutter={[16, 16]} style={{ marginBottom: 16, paddingBottom: 16, borderBottom: '1px solid #f0f0f0' }}>
                     
                     {/* --- NOUVEAU : SÉLECTEUR TYPE --- */}
                     <Col xs={24} sm={12} md={6}>
                         <Text strong><FilterOutlined /> Type:</Text>
                         <Select 
                            style={{width:'100%'}} 
                            placeholder="Tous" 
                            onChange={handleModalPersonTypeChange} 
                            value={selectedModalPersonType} 
                            options={[
                                {value: 'all', label: 'Tous'},
                                {value: '1', label: 'Médecin'},
                                {value: '2', label: 'Pharmacie'},
                                {value: '3', label: 'Grossiste'}
                            ]} 
                         />
                     </Col>
                     {/* ------------------------------- */}

                     <Col xs={24} sm={12} md={6}>
                         <Text strong><UserOutlined /> Nom:</Text>
                         <Select 
                            style={{width:'100%'}} 
                            placeholder="Tous" 
                            loading={loadingModalFilterOptions} 
                            onChange={handleModalDoctorFilterChange} 
                            value={selectedModalDoctorId} 
                            allowClear 
                            showSearch 
                            optionFilterProp="label" 
                            options={doctorModalOptions} 
                         />
                     </Col>
                     <Col xs={24} sm={12} md={6}>
                         <Text strong><ApartmentOutlined /> Spécialité:</Text>
                         <Select 
                            style={{width:'100%'}} 
                            placeholder="Toutes" 
                            loading={loadingModalFilterOptions} 
                            onChange={handleModalSpecialtyFilterChange} 
                            value={selectedModalSpecialtyId} 
                            allowClear 
                            showSearch 
                            optionFilterProp="label" 
                            options={specialtyModalOptions}
                         />
                     </Col>
                     <Col xs={24} sm={12} md={6}>
                         <Text strong><GlobalOutlined /> Région:</Text>
                         <Select 
                            style={{width:'100%'}} 
                            placeholder="Toutes" 
                            loading={loadingModalFilterOptions} 
                            onChange={handleModalRegionFilterChange} 
                            value={selectedModalRegionId} 
                            allowClear 
                            showSearch 
                            optionFilterProp="label" 
                            options={regionModalOptions}
                         />
                     </Col>
                 </Row>
                {errorModalVisits && <Alert message={errorModalVisits} type="error" showIcon closable style={{ marginBottom: 16 }} />}
                <Spin spinning={loadingModalVisits} tip="Chargement visites...">
                   <Title level={5} style={{marginBottom: 10}}>Visites Enregistrées ({modalVisitsPagination.total})</Title>
                   <Table columns={modalVisitColumns} dataSource={delegateVisits} rowKey="id" pagination={false} size="small" scroll={{ y: 300, x: 1000 }} />
                   <Pagination style={{ marginTop: 16, textAlign: 'right' }} current={modalVisitsPagination.current} pageSize={modalVisitsPagination.pageSize} total={modalVisitsPagination.total} onChange={handleModalPageChange} showSizeChanger pageSizeOptions={['10','20','50']} size="small" />
                </Spin>
                <Divider />
                <Spin spinning={loadingMissedDoctors} tip="Analyse médecins non visités...">
                    <Card 
                        size="small" 
                        title={ <Flex justify="space-between" align="center"> <Title level={5} style={{ margin: 0 }}><WarningOutlined style={{color:'orange', marginRight:8}}/> Médecins Non Visités ({modalDelegateName})</Title> {!loadingMissedDoctors && <Text strong>Affichés: {filteredMissedDoctorGroups.length} médecin(s)</Text>} </Flex> }
                        extra={!loadingMissedDoctors && missedDoctorsData.length > 0 && (
                            <Select 
                                value={selectedMissedStatusFilter} onChange={handleMissedStatusFilterChange} size="small" style={{ width: 300 }}
                                options={[
                                    { value: 'all', label: 'Filtrer par statut interaction...' },
                                    { value: OtherInteractionStatusEnum.VISITED_BY_OTHER, label: 'Visité par un autre délégué' },
                                    { value: OtherInteractionStatusEnum.ASSIGNED_TO_OTHER_NOT_VISITED, label: 'Assigné à autre (non visité par eux)' },
                                    { value: OtherInteractionStatusEnum.NOT_ASSIGNED_TO_OTHERS_IN_PERIOD, label: 'Assignation exclusive / Non actif ailleurs' },
                                ]}
                            />
                        )}
                    >
                         {filteredMissedDoctorGroups.length > 0 ? (
                            <AntdList itemLayout="vertical" dataSource={filteredMissedDoctorGroups} size="small" 
                                renderItem={group => {
                                    const visitedByOtherDelegates = group.missedAssignments.flatMap(
                                        assignment => assignment.visitedByOthers?.filter(s => s.visited) || []
                                    );
                                    const assignedToOtherNotVisitedDelegates = group.missedAssignments.flatMap(
                                        assignment => assignment.visitedByOthers?.filter(s => !s.visited) || []
                                    );

                                    return (
                                    <AntdList.Item style={{padding: '10px 0', borderBottom: '1px solid #f0f0f0'}}>
                                        <AntdList.Item.Meta 
                                            avatar={<UserOutlined style={{ fontSize: '1.8em', color: '#595959', paddingTop: '4px' }} />} 
                                            title={
                                                <Flex align="center" gap="small">
                                                    <Text strong style={{fontSize: '1.1em'}}>{group.person.name}</Text> 
                                                    {group.person.specialtyName && group.person.specialtyName !== 'N/A' && 
                                                        <Tag color="processing">{group.person.specialtyName}</Tag>}
                                                </Flex>
                                            }
                                            description={
                                                <>
                                                    <Text type="secondary" style={{display: 'block', marginBottom: 6}}>
                                                        {group.person.regionName || 'Région N/A'}
                                                        {group.person.doctorTypeName && group.person.doctorTypeName !== 'N/A' ? ` - ${group.person.doctorTypeName}`: ''}
                                                    </Text>
                                                    {group.missedAssignments.map(assignment => (
                                                        <div key={`${assignment.delegate.id}-${assignment.cycle.id}`} style={{ marginLeft: '30px', marginBottom: '8px', padding: '8px 10px', borderLeft: '3px solid #d9d9d9', background: '#fafafa', borderRadius: '4px' }}>
                                                            <Text>Manqué avec <Text strong>{assignment.delegate.name}</Text> (Cycle: <Text code>{assignment.cycle.name}</Text> <Text type="secondary" style={{fontSize:'0.85em'}}>({assignment.cyclePeriod})</Text>)</Text>
                                                            
                                                            <div style={{marginTop: '5px'}}>
                                                                <Text strong><TeamOutlined /> Interaction Autres Délégués: </Text>
                                                                {assignment.otherInteractionStatus === OtherInteractionStatusEnum.VISITED_BY_OTHER && visitedByOtherDelegates.length > 0 &&
                                                                    <Tag icon={<CheckCircleOutlined/>} color="success">
                                                                        Visité par: {visitedByOtherDelegates.map(d => d.delegateName).filter((v, i, a) => a.indexOf(v) === i).join(', ')}
                                                                    </Tag>
                                                                }
                                                                {assignment.otherInteractionStatus === OtherInteractionStatusEnum.ASSIGNED_TO_OTHER_NOT_VISITED && assignedToOtherNotVisitedDelegates.length > 0 &&
                                                                    <Tag icon={<WarningOutlined/>} color="warning">
                                                                        Non visité par (mais assigné à): {assignedToOtherNotVisitedDelegates.map(d => d.delegateName).filter((v, i, a) => a.indexOf(v) === i).join(', ')}
                                                                    </Tag>
                                                                }
                                                                {assignment.otherInteractionStatus === OtherInteractionStatusEnum.NOT_ASSIGNED_TO_OTHERS_IN_PERIOD &&
                                                                    <Tag icon={<InfoCircleOutlined />} color="default">
                                                                        Non actif avec d'autres délégués sur cette période
                                                                    </Tag>
                                                                }
                                                                {assignment.otherInteractionStatus === null && 
                                                                    <Text type="secondary" style={{fontStyle: 'italic'}}>(Pas d'information d'interaction)</Text>
                                                                }
                                                            </div>
                                                        </div>
                                                    ))}
                                                </>
                                            }
                                        />
                                    </AntdList.Item>
                                    );
                                }} 
                                style={{ maxHeight: '300px', overflowY: 'auto', paddingRight: '10px' }} 
                            />
                         ) : ( <Text type="secondary" style={{display:'block', textAlign:'center', padding: '20px'}}>{loadingMissedDoctors ? 'Analyse...' : 'Aucun médecin non visité correspondant aux filtres.'}</Text> )}
                    </Card>
                </Spin>
            </Modal>
        </>
    );
};
export default VisitManagementPage;